import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function PlotViewer( {selectedDate, selectedCampaign} ){
  const [selectedPlot, setSelectedPlot] = useState(null);  // Plot for the selected date
  const [plotType, setPlotType] = useState(null);  // Type of plot (html or png)
  const [integrationTime, setIntegrationTime] = useState(null);  // Selected integration time (e.g. '20MIN' or 'default')
  const [availableIntegrationTimes, setAvailableIntegrationTimes] = useState([]);

  // Detect if user is on mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  // Fetch the available integration times for the selected date and campaign.
  // If the currently selected time isn't available anymore, fall back to the
  // shortest/default one (the list is pre-sorted by the server).
  useEffect(() => {
    if (!selectedDate || !selectedCampaign) {
      setAvailableIntegrationTimes([]);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    fetch(`${API_BASE}/api/plot-times/${selectedCampaign}/${formattedDate}`, {
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch available integration times'))
      .then(data => {
        setAvailableIntegrationTimes(data.times);
        setIntegrationTime(prev => (data.times.some(t => t.value === prev) ? prev : (data.times[0]?.value ?? null)));
      })
      .catch(err => {
        console.error('Error fetching available integration times:', err);
        setAvailableIntegrationTimes([]);
      });
  }, [selectedDate, selectedCampaign]);

  // Fetch the plot for selected date, campaign, and integration time
  useEffect(() => {
    if (!selectedDate || !selectedCampaign) return;

    const formattedDate = format(selectedDate, 'yyyy-MM-dd') // selectedDate.toISOString().split('T')[0];
    const params = new URLSearchParams();
    if (isMobile()) params.set('mobile', 'true');
    if (integrationTime) params.set('integration', integrationTime);

    fetch(`${API_BASE}/api/plot/${selectedCampaign}/${formattedDate}?${params.toString()}`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Plot not found'))
      .then(data => {
        setSelectedPlot(`${API_BASE}${data.original}`);
        setPlotType(data.type);
      })
      .catch(err => {
        console.error('Error fetching selected plot:', err);
        setSelectedPlot(null);
        setPlotType(null);
      });
  }, [selectedDate, selectedCampaign, integrationTime]);

  return (
    <div className="content-card">
      <h2>Quicklooks</h2> 
      
      {(selectedCampaign === 'Test' || selectedCampaign === 'Kuehlungsborn' || selectedCampaign === 'Test_Andoya') && (
        <div className="warning-banner">
          ⚠️ Warning - currently with dummy lidar data!
        </div>
      )}

      {selectedPlot ? (
        <div>
          <div className="plot-controls">
            <h4 style={{ margin: 0 }}>Quicklook for {selectedCampaign} — {format(selectedDate, 'yyyy-MM-dd')}</h4>
            {availableIntegrationTimes.length > 1 && (
              <select
                className="modern-select"
                value={integrationTime || ''}
                onChange={(e) => setIntegrationTime(e.target.value)}
              >
                {availableIntegrationTimes.map(time => (
                  <option key={time.value} value={time.value}>
                    {time.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          {plotType === 'html' && (
            <p className="interactive-indicator">
              ✨ Interactive plot (zoomable)
            </p>
          )}
          <div className="plot-container" style={{ maxWidth: plotType === 'html' ? '100%' : '1200px' }}>
            {plotType === 'html' ? (
              <iframe 
                src={selectedPlot} 
                title="Interactive Plot" 
                className="interactive-plot-iframe"
              />
            ) : (
              <img 
                src={selectedPlot} 
                alt="Selected Plot" 
                style={{ width: '100%', height: 'auto', borderRadius: '8px' }} 
              />            
            )}
          </div>
        </div>
      ) : (selectedDate && selectedCampaign) ? (
        <div className="empty-state">
          <p>No plot available for this date and campaign site.</p>
        </div>
      ) : !selectedCampaign ? (
        <div className="empty-state">
          <p>Please select a campaign site.</p>
        </div>
      ) : null}
    </div>
  );
};

export default PlotViewer;
